import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t, language } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const getPath = (path) => {
    return language === 'en' ? `/en${path}` : path;
  };

  const navLinks = [
    { name: t.nav.home, path: getPath('/') },
    { name: t.nav.services, path: getPath('/services') },
    { name: t.nav.about, path: getPath('/about') },
    { name: t.nav.contact, path: getPath('/contact') }
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-black/95 backdrop-blur-md border-b border-[#D6CE15]/20' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img 
              src="/logo.png" 
              alt="13Labs" 
              className="h-8 md:h-10 w-auto transition-all duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-all duration-200 ${
                  location.pathname === link.path
                    ? 'text-[#D6CE15] neon-text'
                    : 'text-gray-400 hover:text-[#D6CE15]'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Language Switcher */}
          <div className="hidden md:flex items-center space-x-4">
            <LanguageSwitcher />
            
            {/* CTA Button */}
            <Link
              to={getPath('/contact')}
              className="px-6 py-2.5 bg-[#D6CE15] text-black rounded-lg font-semibold text-sm hover:bg-[#A4A71E] transition-all duration-200 hover:shadow-xl hover:shadow-[#D6CE15]/50 hover:-translate-y-0.5"
            >
              {t.buttons.getStarted}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-gray-300 hover:text-white transition-colors"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-black border-t border-[#D6CE15]/20 shadow-2xl">
            <nav className="flex flex-col space-y-4 px-6 py-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`text-base font-medium transition-colors duration-200 py-2 ${
                    location.pathname === link.path
                      ? 'text-[#D6CE15] neon-text'
                      : 'text-gray-300 hover:text-[#D6CE15]'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              {/* Mobile Language Switcher */}
              <div className="py-2 border-t border-white/10 mt-2">
                <LanguageSwitcher />
              </div>
              
              <Link
                to={getPath('/contact')}
                onClick={() => setIsMobileMenuOpen(false)}
                className="inline-block px-6 py-3 bg-[#D6CE15] text-black rounded-lg font-semibold text-sm text-center hover:bg-[#A4A71E] transition-all duration-200 shadow-lg shadow-[#D6CE15]/30"
              >
                {t.buttons.getStarted}
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;